/**
 * Zählregel pro Webhook-Datenquelle (TASK-187) — plain-text options, the
 * day-field validator and the Purchase revenue hint condition behind the
 * Anlege-Formular and die Detailseite. Mirrors the server-side validation
 * in apps/api/src/lib/webhook-endpoints.ts (parseCountRuleInput).
 */

export type CountRule = "every" | "first" | "interval";

export const COUNT_RULE_OPTIONS: { value: CountRule; label: string }[] = [
  { value: "every", label: "Jedes Event zählen" },
  { value: "first", label: "Nur das erste Event pro Kontakt zählen" },
  { value: "interval", label: "Einmal alle X Tage zählen" },
];

export const DEFAULT_COUNT_INTERVAL_DAYS = 30;

export type CountIntervalDaysValidation =
  | { ok: true; value: number | null }
  | { ok: false; message: string };

/** The days field only exists (and only matters) when the rule is
 *  'interval' — for 'every'/'first' any raw input is simply ignored. */
export function validateCountIntervalDaysInput(
  raw: string,
  countRule: CountRule,
): CountIntervalDaysValidation {
  if (countRule !== "interval") return { ok: true, value: null };
  const trimmed = raw.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) {
    return { ok: false, message: "Tage-Zahl muss eine ganze Zahl zwischen 1 und 365 sein." };
  }
  const days = Number(trimmed);
  if (days < 1 || days > 365) {
    return { ok: false, message: "Tage-Zahl muss eine ganze Zahl zwischen 1 und 365 sein." };
  }
  return { ok: true, value: days };
}

export function shouldShowPurchaseHint(eventType: string, countRule: CountRule): boolean {
  return eventType === "Purchase" && countRule !== "every";
}

/**
 * G3 fix (Station-5-Gap, Epic v2 A5) — the retro warn-dialog's actual
 * question, previously missing entirely (only the two answer buttons were
 * rendered). Extracted as a constant, not inlined in the .tsx, so the exact
 * wording the PRD/Epic mandates is unit-testable at the text level (see
 * apps/admin/tests/count-rule-retro-dialog.test.ts) without a rendering test.
 */
export const COUNT_RULE_RETRO_QUESTION =
  "Möchtest du alle bereits gespeicherten Events mit diesem Namen im Projekt rückwirkend nach der neuen Regel umbenennen?";

/**
 * G3 fix — the red warning must say explicitly that this reaches ALL events
 * of that name in the project (Website-Formulare, Importe eingeschlossen),
 * not only the events of the webhook source being edited (Epic v2 A5, the
 * v2 change from v1's "nur diese Quelle" assumption).
 */
export const COUNT_RULE_RETRO_WARNING =
  "Betroffen sind ALLE Events mit diesem Namen im Projekt, auch aus Website-Formularen und " +
  "Importen — nicht nur die dieser Datenquelle. Das verändert rückwirkend Statistiken und " +
  "Auswertungen, auch bereits an Kunden berichtete Zahlen, und lässt sich nur durch erneutes " +
  "Umstellen wieder angleichen.";

/**
 * S8-1: the retro warn-dialog only shows when rule OR days actually
 * changed on an EXISTING source — never on create (the create form never
 * calls this). Days only matter for 'interval'; comparing null-vs-30
 * between two non-interval rules would be a false positive.
 */
export function countRuleChanged(
  current: { rule: CountRule; intervalDays: number | null },
  next: { rule: CountRule; intervalDays: number | null },
): boolean {
  if (current.rule !== next.rule) return true;
  if (next.rule !== "interval") return false;
  return current.intervalDays !== next.intervalDays;
}

/**
 * S8-4 + review finding (S7) — plain-text status line for a resync run's
 * "done" state distinguishes three cases: nothing left in the mirror to
 * catch up, a catch-up still pending/running, or one that gave up
 * (error_class set by the worker's failed-handler — see
 * apps/worker/src/lib/count-rule-resync.ts:recordCountRuleResyncFailure).
 */
export function countRuleResyncRunLabel(run: {
  status: "running" | "done" | "failed";
  renamed_count: number;
  deferred_count: number;
  error_class: string | null;
}): string {
  if (run.status === "running") return "läuft…";
  if (run.status === "failed") {
    return `fehlgeschlagen${run.error_class ? ` (${run.error_class})` : ""}`;
  }
  const base = `fertig — ${run.renamed_count} Event(s) umbenannt`;
  if (run.deferred_count === 0) return base;
  if (run.error_class) {
    return `${base}, Nachzug im Spiegel fehlgeschlagen (${run.error_class})`;
  }
  return `${base}, Nachzug für ${run.deferred_count} im Spiegel läuft/ausstehend`;
}
